import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { esitoUpload } from "@/lib/upload";
import { Save, ClipboardList, UploadCloud, Loader2, FileText, FileImage, ExternalLink, ScanLine } from "lucide-react";
import { useExtraction } from "@/context/ExtractionContext";

const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "");
const isPdf = (m) => (m || "").includes("pdf");

// Campi specifici del tipo di pratica (es. dati dello Sponsor per il Visto Studio):
// i valori vivono NELLA PRATICA, non nell'anagrafica del cliente.
// Il riquadro prende il nome della sezione configurata (es. "Sponsor") e permette di
// allegare i relativi documenti nella cartella Drive del cliente.
export default function PraticaCampi({ pratica, onChanged, senzaCornice }) {
  const qc = useQueryClient();
  const { jobs, start, claim, watch } = useExtraction();
  const [jobId, setJobId] = useState(null);
  const campi = pratica.campi_pratica || [];
  const cliente = pratica.cliente || {};
  const inputRef = useRef(null);
  const ultimoDrop = useRef(0);
  const [dati, setDati] = useState(pratica.dati_pratica || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { setDati(pratica.dati_pratica || {}); setDirty(false); }, [pratica.id, pratica.dati_pratica]);

  const job = jobs.find((j) => j.id === jobId) || null;
  const leggendo = job?.stato === "in_corso";

  useEffect(() => { if (jobId) { watch(jobId, true); return () => watch(jobId, false); } }, [jobId, watch]);

  useEffect(() => {
    if (!job) return;
    if (job.stato === "completo") {
      const sug = job.result?.campi_pratica_suggeriti || {};
      claim(job.id); setJobId(null);
      const n = Object.keys(sug).length;
      if (n === 0) {
        toast.warning("Nessun dato riconosciuto nei documenti: compila a mano.");
      } else {
        setDati((prec) => ({ ...prec, ...sug }));
        setDirty(true);
        toast.success(`${n} campo/i compilato/i dai documenti. Controlla e premi Salva.`);
      }
    } else if (job.stato === "errore") {
      toast.error(job.messaggio || "Lettura non riuscita");
      claim(job.id); setJobId(null);
    }
  }, [job, claim]);

  const gruppi = [];
  // i campi dello sponsor compaiono solo se la pratica ha dichiarato uno sponsor
  const visibili = pratica.ha_sponsor
    ? campi
    : campi.filter((c) => !/sponsor|segnalatore/i.test(`${c.nome || ""} ${c.gruppo || ""}`));
  visibili.forEach((c) => {
    const g = c.gruppo || "Dati della pratica";
    const found = gruppi.find((x) => x.nome === g);
    if (found) found.campi.push(c); else gruppi.push({ nome: g, campi: [c] });
  });
  const unico = gruppi.length === 1 ? gruppi[0].nome : null;
  const titolo = unico || "Dati specifici della pratica";
  const cartella = unico || "Documenti pratica";

  const { data: driveData } = useQuery({
    queryKey: ["drive-files", cliente.id],
    queryFn: async () => (await api.get(`/clienti/${cliente.id}/drive-files`)).data,
    enabled: !!cliente.id && campi.length > 0,
  });
  const allegati = (driveData?.files || []).filter((f) => f.categoria === cartella);

  if (campi.length === 0 || visibili.length === 0) {
    return (
      <p data-testid="campi-pratica-vuoti" className="text-[13px] text-[#5A5A5A]">
        Per questo tipo di pratica non servono altri dati. I campi dello sponsor compaiono solo se lo
        dichiari dal menù «⋯».
      </p>
    );
  }

  const mancanti = campi.filter((c) => c.obbligatorio && !String(dati[c.key] || "").trim()).length;

  const salva = async () => {
    setSaving(true);
    try {
      await api.put(`/pratiche/${pratica.id}/dati`, { dati });
      toast.success(`Dati ${unico ? unico.toLowerCase() : "della pratica"} salvati`);
      setDirty(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  const leggiConAI = async (files) => {
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("pratica_id", pratica.id);
      setJobId(await start({ url: "/clienti/estrai-documenti/start", payload: fd, files, label: titolo }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lettura AI non avviata");
    }
  };

  const leggiDaDrive = async () => {
    if (allegati.length === 0) return toast.error("Nessun documento in questa sezione da leggere.");
    try {
      setJobId(await start({
        url: `/clienti/${cliente.id}/estrai-da-drive/start`,
        payload: { file_ids: allegati.slice(0, 8).map((f) => f.id), pratica_id: pratica.id,
                   files: allegati.slice(0, 8).map((f) => ({ id: f.id, name: f.name })) },
        files: [], label: titolo,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lettura AI non avviata");
    }
  };

  const carica = async (list) => {
    const files = Array.from(list || []);
    if (files.length === 0) return;
    if (!cliente.id) return toast.error("Cliente non disponibile");
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => { fd.append("files", f); fd.append("names", stripExt(f.name)); });
      fd.append("categoria", cartella);
      const { data } = await api.post(`/clienti/${cliente.id}/upload`, fd);
      toast.success(esitoUpload(data, `Cartella "${cartella}"`));
      qc.invalidateQueries({ queryKey: ["drive-files", cliente.id] });
      await leggiConAI(files);  // lettura AI automatica dei documenti appena trascinati
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="pratica-campi-box" className={senzaCornice ? "" : "bg-white border border-[#1A2B4A]/10 rounded-sm p-4"}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#C9A84C]" />
          {!senzaCornice && <h3 data-testid="pratica-campi-titolo" className="font-serif text-xl text-[#1A2B4A]">{titolo}</h3>}
        </div>
        <div className="flex items-center gap-2">
        {leggendo ? (
          <span data-testid="pratica-campi-leggendo" className="inline-flex items-center gap-1.5 text-xs text-[#8a6d1f]">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C9A84C]" /> Lettura AI… {job.fatti || 0}/{job.totale}
          </span>
        ) : (
          <button data-testid="pratica-campi-leggi-ai" onClick={leggiDaDrive}
            className="inline-flex items-center gap-1.5 text-xs border border-[#C9A84C] text-[#8a6d1f] bg-white px-3 py-1.5 rounded-sm hover:bg-[#faf5e6] transition-colors">
            <ScanLine className="w-3.5 h-3.5" /> Leggi documenti (AI)
          </button>
        )}
        {dirty && (
          <button data-testid="pratica-campi-save" onClick={salva} disabled={saving}
            className="inline-flex items-center gap-1.5 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-sm hover:bg-[#22345c] disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> Salva
          </button>
        )}
        </div>
      </div>
      {mancanti > 0 && (
        <p data-testid="pratica-campi-mancanti" className="text-xs text-[#8a6d1f] bg-[#fbf7ec] border border-[#C9A84C]/40 rounded-sm px-3 py-2 mb-4">
          {mancanti} campo/i obbligatorio/i ancora da compilare per questa pratica.
        </p>
      )}
      <div className="space-y-5">
        {gruppi.map((g) => (
          <div key={g.nome} data-testid={`pratica-campi-gruppo-${g.nome}`}>
            {!unico && <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#4a5568] mb-2">{g.nome}</p>}
            <div className="grid sm:grid-cols-2 gap-3">
              {g.campi.map((c) => (
                <div key={c.key}>
                  <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#8a93a6]">
                    {c.label}{c.obbligatorio && <span className="text-[#9b2c2c]"> *</span>}
                  </label>
                  <input data-testid={`pratica-campo-${c.key}`} value={dati[c.key] || ""}
                    onChange={(e) => { setDati({ ...dati, [c.key]: e.target.value }); setDirty(true); }}
                    className="w-full mt-1 px-3 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div
        data-testid="pratica-campi-dropzone"
        onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => { e.preventDefault(); if (!uploading) setDrag(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); ultimoDrop.current = Date.now(); setDrag(false); if (!uploading) carica(e.dataTransfer.files); }}
        onClick={(e) => {
          if (!e.currentTarget.contains(e.target)) return;      // clic da finestra sovrapposta
          if (Date.now() - ultimoDrop.current < 1500) return;   // clic generato dal rilascio
          if (!uploading) inputRef.current?.click();
        }}
        className={`mt-6 cursor-pointer rounded-sm border-2 border-dashed px-6 py-7 text-center transition-colors ${
          drag ? "border-[#C9A84C] bg-[#f7edcf]" : "border-[#C9A84C]/50 bg-[#fbf7ec]/60 hover:bg-[#faf5e6]"
        }`}>
        {uploading ? (
          <p data-testid="pratica-campi-uploading" className="flex items-center justify-center gap-2 text-sm text-[#1A2B4A]">
            <Loader2 className="w-4 h-4 animate-spin text-[#C9A84C]" /> Caricamento su Drive…
          </p>
        ) : (
          <div className="space-y-2.5">
            <UploadCloud className={`w-8 h-8 mx-auto ${drag ? "text-[#C9A84C]" : "text-[#C9A84C]/70"}`} />
            <p className="text-sm text-[#1A2B4A] font-medium">
              Trascina qui i documenti {unico ? `dello ${unico.toLowerCase()}` : "della pratica"}
            </p>
            <p className="text-[11px] text-[#4a5568]">
              Finiscono nella cartella Drive del cliente (sottocartella “{cartella}”) e vengono letti dall'AI per compilare i campi
            </p>
            <button type="button" data-testid="pratica-campi-upload-btn" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              className="inline-flex items-center gap-2 text-xs border border-[#C9A84C] text-[#8a6d1f] bg-white px-3 py-2 rounded-sm hover:bg-[#faf5e6] transition-colors">
              <UploadCloud className="w-4 h-4" /> Carica documenti
            </button>
          </div>
        )}
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx" className="hidden"
          data-testid="pratica-campi-file-input" onChange={(e) => carica(e.target.files)} />
      </div>

      {allegati.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="pratica-campi-allegati">
          {allegati.map((f) => {
            const Icon = (f.mimeType || "").startsWith("image/") ? FileImage : FileText;
            return (
              <li key={f.id}>
                <a href={f.webViewLink} target="_blank" rel="noreferrer" data-testid={`pratica-campi-allegato-${f.id}`}
                  className="flex items-center gap-2.5 border border-[#1A2B4A]/10 rounded-sm px-3 py-2 hover:border-[#C9A84C] hover:bg-[#f9f8f5] transition-colors group">
                  <Icon className={`w-4 h-4 shrink-0 ${isPdf(f.mimeType) ? "text-[#9b2c2c]" : "text-[#1A2B4A]/60"}`} />
                  <span className="text-sm text-[#1A2B4A] truncate flex-1" title={f.name}>{f.name}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-[#4a5568] group-hover:text-[#1A2B4A] shrink-0" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
